/**
 * H2-F — Creator: Holder redemption requests. The creator confirms fulfilment
 * (or explains why it can't be fulfilled). No payouts or funds involved.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { requested: 'Awaiting you', fulfilled: 'Fulfilled', declined: "Couldn't fulfil" };

export default function CreatorRedemptionsPanel({ portfolioId, auth }) {
  const [rows, setRows] = useState(null);
  const [notes, setNotes] = useState({});
  const [err, setErr] = useState({});

  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/creator/portfolios/${portfolioId}/redemptions`, auth)).data.redemptions || []); }
    catch { setRows([]); }
  }, [portfolioId, auth]);
  useEffect(() => { load(); }, [load]);

  const record = async (id, outcome) => {
    setErr({ ...err, [id]: null });
    try {
      await axios.post(`${API}/creator/portfolios/${portfolioId}/redemptions/${id}/fulfilment`, { outcome, note: notes[id] || '' }, auth);
      toast.success(outcome === 'fulfilled' ? 'Marked as fulfilled' : 'Marked as not fulfilled');
      load();
    } catch (e) { setErr({ ...err, [id]: e?.response?.data?.detail || 'Could not save' }); }
  };

  if (!rows) return null;
  return (
    <div data-testid="creator-redemptions" style={{ marginTop: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 15 }}>Redemption requests <span style={{ fontSize: 12, color: theme.colors.text.muted }}>({rows.filter(r => r.status === 'requested').length} awaiting you)</span></div>
      <div style={{ fontSize: 12, color: theme.colors.text.muted, margin: '2px 0 8px' }}>Holders see "Fulfilled" only after you confirm it here. If you can't fulfil a request, add a note; the use is returned to the Holder.</div>
      {rows.length === 0 && <div data-testid="creator-redemptions-empty" style={{ fontSize: 13, color: theme.colors.text.muted }}>No redemption requests yet.</div>}
      {rows.map(r => (
        <div key={r.id} data-testid={`creator-redemption-${r.id}`} style={{ padding: '8px 0', borderTop: `1px solid ${theme.colors.borders}`, fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ flex: 1, minWidth: 220 }}><b>{r.utility_title}</b> · {r.holder_email || 'Holder'} · {new Date(r.redeemed_at).toLocaleString()} · terms v{r.terms_version}</span>
            <span data-testid={`creator-redemption-status-${r.id}`} style={{ fontSize: 11, fontWeight: 800, color: r.status === 'requested' ? '#92400E' : r.status === 'fulfilled' ? '#065F46' : '#991B1B' }}>{LABEL[r.status] || r.status}</span>
          </div>
          {r.status === 'requested' ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <input data-testid={`creator-redemption-note-${r.id}`} placeholder="Note for the Holder (required if you can't fulfil)" value={notes[r.id] || ''}
                onChange={e => setNotes({ ...notes, [r.id]: e.target.value })}
                style={{ flex: 1, minWidth: 220, padding: '6px 8px', border: `1px solid ${theme.colors.borders}`, borderRadius: 6, fontSize: 12 }} />
              <button data-testid={`creator-redemption-fulfil-${r.id}`} onClick={() => record(r.id, 'fulfilled')} style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: '#065F46', color: '#fff', fontSize: 12, cursor: 'pointer' }}>Mark fulfilled</button>
              <button data-testid={`creator-redemption-decline-${r.id}`} onClick={() => record(r.id, 'declined')} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #991B1B', background: '#fff', color: '#991B1B', fontSize: 12, cursor: 'pointer' }}>Can't fulfil</button>
            </div>
          ) : r.creator_note && <div style={{ fontSize: 12, color: theme.colors.text.muted }}>Note: {r.creator_note}</div>}
          {err[r.id] && <div style={{ fontSize: 12, color: '#991B1B' }}>{err[r.id]}</div>}
        </div>
      ))}
    </div>
  );
}
